import { getCleanedSearchUrl } from "../../lib/search-utils";

export const GROUPING_ABTEST_QUERY_KEY = "updated-size-group-for-a-b-test";

// PARSES THE FACET NAV DATA
export const getDerivedFacetData = ({
  facetItemData,
  isMobile,
  isTruncatedPanelExpanded,
  isTabletOrBelow,
  selectedSortTitle,
  collectionExplainerABFlag = false,
}) => {
  const {
    name,
    type,
    refinements,
    enableVisualSizeStyling,
    enableVisualColorStyling,
    "multi-select": isMultiSelect,
    showMoreRefinementsLink: enableShowMoreButton,
    "selected-count": selectedCount,
    primaryNumRefinements,
    noFollow,
  } = facetItemData;

  const shouldHideViewMoreButton =
    collectionExplainerABFlag && name?.toLowerCase() === "collection";

  const hasName = !!name && typeof name === "string";

  const isSort = type === "SortOptions";

  const hasNoFollow = noFollow === "1";

  const showMobileFilterSort = isTabletOrBelow;
  const shouldHideMobileFilterSortButton = isSort && !showMobileFilterSort;

  const cleanedName = hasName && name.replace(/ /g, "-");

  const isTrucatedFilterExpanded = !!isTruncatedPanelExpanded?.({
    panelName: cleanedName,
  });

  const hasSizeOrColor = enableVisualSizeStyling || enableVisualColorStyling;

  const itemLength = refinements?.length || 0;

  const shouldDisplayTruncatedFilters = !!(
    enableShowMoreButton &&
    primaryNumRefinements &&
    primaryNumRefinements < itemLength &&
    !hasSizeOrColor &&
    !shouldHideViewMoreButton
  );

  const filterNameWithCount =
    selectedCount > 0 && isTabletOrBelow ? `${name} (${selectedCount})` : name;

  const parsedAccordionName = isSort
    ? `${name} ${selectedSortTitle}`
    : filterNameWithCount;

  const shouldDisplayViewMoreButton =
    (enableShowMoreButton || !shouldHideViewMoreButton) &&
    shouldDisplayTruncatedFilters;

  const accordionItemProps = {
    heading: parsedAccordionName,
    headingTag: "h2",
    key: `${cleanedName}-AccordionItem`,
    name: name,
    value: selectedSortTitle,
    stateKey: cleanedName,
  };

  const facetItemProps = {
    enableVisualColorStyling,
    enableVisualSizeStyling,
    isSort,
    isMultiSelect,
    hasNoFollow,
    name,
    primaryNumRefinements,
    isTrucatedFilterExpanded,
    shouldDisplayTruncatedFilters,
  };

  const viewMoreButtonProps = {
    panelName: cleanedName,
    primaryNumRefinements,
    facetLength: facetItemData?.length,
  };

  return {
    name,
    parsedName: cleanedName,
    refinements,
    enableVisualSizeStyling,
    enableVisualColorStyling,
    primaryNumRefinements,
    isTrucatedFilterExpanded,
    shouldDisplayViewMoreButton,
    shouldDisplayTruncatedFilters,
    isMultiSelect,
    shouldHideMobileFilterSortButton,
    isSort,
    facetItemProps,
    accordionItemProps,
    selectedCount,
    hasNoFollow,
    viewMoreButtonProps,
    itemLength,
    hasSizeOrColor,
  };
};

// PARSES THE FACET ITEM NAV DATA
export const getDerivedFacetItemData = ({
  refinement,
  enableVisualSizeStyling,
  enableVisualColorStyling,
  isMultiSelect,
  index,
  primaryNumRefinements,
  isTrucatedFilterExpanded,
  fusionSerp,
  shouldDisplayTruncatedFilters,
}) => {
  const { label: facetItemName } = refinement;
  const defaultPrimaryNumRefinements =
    primaryNumRefinements === "" ? null : primaryNumRefinements;

  const hasSizeOrColor = enableVisualSizeStyling || enableVisualColorStyling;
  // TODO LOOK AT MOVING THIS TO BE/GRAPH
  const facetLabel = refinement.label.split("|")[0];
  const CFSizeArialabels =
    facetLabel.includes(" C") || facetLabel.includes(" F")
      ? facetLabel.toLowerCase()
      : undefined;
  const navigationUrl = refinement.isSelected
    ? refinement["remove-action"]["navigation-state"]
    : refinement["navigation-state"];

  // MOVE THIS TO THE DATA SIDE
  const navigationState = fusionSerp
    ? getCleanedSearchUrl(navigationUrl)
    : navigationUrl;

  const isIndexHidden =
    shouldDisplayTruncatedFilters &&
    !!defaultPrimaryNumRefinements &&
    index >= defaultPrimaryNumRefinements;

  // FOR ACCESSIBILITY MAKES THE ELEMENT TABBABLE OR NOT
  const isElementTabbable =
    !isTrucatedFilterExpanded && isIndexHidden ? -1 : null;

  const isElementHidden =
    !isTrucatedFilterExpanded && isIndexHidden ? "hidden" : "visible";

  const isDefaultHidden = isIndexHidden ? "hidden" : "visible";

  // THIS SHOULD BE COMING FROM THE DATA
  const finalURL = `${refinement["content-path"]}${navigationState}`;

  let facetType = "link";

  if (!hasSizeOrColor && isMultiSelect) facetType = "checkbox";
  if (enableVisualColorStyling) facetType = "color";
  if (enableVisualSizeStyling) facetType = "size";

  return {
    finalURL,
    CFSizeArialabels,
    facetType,
    facetItemName,
    isElementTabbable,
    isElementHidden,
    isDefaultHidden,
  };
};

// CLEANS UP THE ENDECA RESPONSE

// SEE IF WE CAN STRIP THIS OUT IN GRAPHQL
export const parseFacetData = ({ facetData }) => {
  const parsedFacetData = facetData?.filter(({ type, name }) => {
    return (
      type !== "StaticRefinementTreeMenu" &&
      type !== "CDPNavRefinementMenu" &&
      name !== "category"
    );
  });

  return {
    parsedFacetData,
  };
};

/**
 * Retrieves the two most popular facets from the provided facets data.
 *
 * This function parses the input facets data, filters out any facets of type
 * "SortOptions", and returns an array containing the first two popular facets.
 *
 * @param {Array<Object>} facets - An array of facet objects. Each object is expected
 *                                  to contain at least a 'type' property.
 */

export const getPopularFilters = (facets = []) => {
  const parsedData = parseFacetData({ facetData: facets });
  const popularFilters = parsedData?.parsedFacetData?.filter(({ type }) => {
    return type !== "SortOptions";
  });
  return popularFilters?.slice(0, 2) || [];
};

// SENDS SIGNALS TO FUSION
export const sendFilterClickSignals = ({
  accordionTitleName,
  facetItemName,
  fusionQueryId,
}) => {
  const type = accordionTitleName.toLowerCase();
  if (type === "size" || type === "colour" || type === "color") {
    const fields = {
      type: "filter",
      filter_name: accordionTitleName,
      filter_value: facetItemName,
      fusion_query_id: fusionQueryId,
    };
  }
};

/**
 * Sorts an array of alpha size objects based on their alphabetical and numerical properties.
 *
 * Each size object is expected to have a `label` property
 * The sorting takes into account specific rules for determining the sorting points:
 * - 'M' is given the lowest point value.
 * - Numeric sizes are converted to points based on their ASCII values.
 * - Sizes with 'S' are treated differently during sorting.
 * - Sizes with a dual label (containing a '/') receive a slight point boost.
 *
 * @param {Array<Object>} sizes - An array of size objects to be sorted.
 * @returns {Array<Object>} - A new array of size objects sorted by calculated points.
 */
export const sortAlphaSizes = (sizes) => {
  if (!sizes?.length) return [];
  return sizes
    .map((size) => {
      const label = size.label;
      const [baseSize] = label.split("/");
      const isM = baseSize === "M";
      const firstCharCode = baseSize.charCodeAt(0);
      const hasNumbers = !isNaN(baseSize[0]);
      const basePoints = isM ? 0 : hasNumbers ? firstCharCode : 1;
      const lengthFactor = label.length;
      const direction = baseSize.includes("S") ? -1 : 1;
      // We're using 570 as the factor from 57 (char code for 9 (last number)) * 10
      // to send dual alpha sizes to the last positions
      const dualAlphaPoints = label.includes("/") ? 570 : 0;

      return {
        ...size,
        points: basePoints * lengthFactor * direction + dualAlphaPoints,
      };
    })
    .sort((a, b) => a.points - b.points);
};

/**
 * Sorts an array of size objects first alphabetically and then numerically.
 *
 * The function uses the `sortAlphaSizes` helper function to perform an initial
 * sort for alpha sizes. After that, it sorts the sizes based on whether their labels
 * are numeric or not, handling specific cases such as "ONE SIZE".
 *
 * Numeric labels are sorted in ascending order, while non-numeric labels are sorted
 * based on their alphabetical order. The label "ONE SIZE" is placed at the end of the sorted list.
 *
 * @param {Array<Object>} sizes - An array of size objects to be sorted.
 * @returns {Array<Object>} - A new array of size objects sorted
 */
export const sortAlphaAndNumericSizes = (sizes) => {
  if (!sizes?.length) return [];
  const alphaSorted = sortAlphaSizes(sizes);
  return alphaSorted.sort((a, b) => {
    const isANumber = !isNaN(a.label);
    const isBNumber = !isNaN(b.label);

    if (isANumber && isBNumber) {
      return parseFloat(a.label) - parseFloat(b.label); // Sort numerically
    }

    if (isANumber) return -1; // a comes first
    if (isBNumber) return 1; // b comes first

    if (a.label === "ONE SIZE") return 1; // "ONE SIZE" comes last
    if (b.label === "ONE SIZE") return -1; // "ONE SIZE" comes last
  });
};

/**
 * Sorts an array of waist size objects based on their labels, which may include numeric and alphabetic characters.
 *
 * The function sorts sizes in ascending order. Numeric labels are compared directly,
 * while alphabetic labels (16X, 18X, ..., 22X) are placed after numeric labels.
 *
 * @param {Array<Object>} sizes - An array of waist size objects to be sorted.
 * @returns {Array<Object>} - A new array of waist size objects sorted.
 */
export const sortWaistSizes = (sizes) => {
  if (!sizes?.length) return [];
  return sizes.sort((a, b) => {
    const hasAlphaCharA = isNaN(a.label);
    const hasAlphaCharB = isNaN(b.label);

    if (hasAlphaCharA && !hasAlphaCharB) return 1;
    if (!hasAlphaCharA && hasAlphaCharB) return -1;

    return parseInt(a.label) - parseInt(b.label);
  });
};

/**
 * Sorts an array of bra size objects based on their band and cup sizes.
 *
 * The function uses a regular expression to extract the numeric band size and alphabetic cup size
 * from the labels. Sizes are sorted primarily by band size (numerically), and if the band sizes are
 * the same, they are sorted by cup size (alphabetically).
 *
 * @param {Array<Object>} sizes - An array of bra size objects to be sorted.
 * @returns {Array<Object>} - A new array of bra size objects sorted by band and cup sizes.
 */
export const sortBraSizes = (sizes) => {
  if (!sizes?.length) return [];
  return sizes.sort((a, b) => {
    const bandCupRegex = /(\d+)\s?([A-Z]+)/;
    const [, bandA, cupA] = bandCupRegex.exec(a.label) || [];
    const [, bandB, cupB] = bandCupRegex.exec(b.label) || [];

    // Compare band sizes as numbers
    const bandComparison = parseInt(bandA) - parseInt(bandB);
    if (bandComparison !== 0) return bandComparison;

    return cupA.localeCompare(cupB);
  });
};

export const sortSizeGroups = (sizeGroups) => {
  if (!Object.keys(sizeGroups).length) return {};
  // Each key in the sizeGroups object represents the size group
  const {
    1: numericAndAlpha,
    2: waist,
    3: spaceBra,
    4: bra,
    5: shoes,
    ...otherGroups
  } = sizeGroups;
  const sortedNumericAndAlpha = sortAlphaAndNumericSizes(numericAndAlpha);
  const sortedWaist = sortWaistSizes(waist);
  const sortedSpaceBra = sortBraSizes(spaceBra);
  const sortedBra = sortBraSizes(bra);
  const sortedShoes = shoes?.sort((a, b) => a.label - b.label);
  // Adds the data for the size group if exists
  return {
    ...(!!sortedNumericAndAlpha.length && { 1: sortedNumericAndAlpha }),
    ...(!!sortedWaist.length && { 2: sortedWaist }),
    ...(!!sortedSpaceBra.length && { 3: sortedSpaceBra }),
    ...(!!sortedBra.length && { 4: sortedBra }),
    ...(!!sortedShoes?.length && { 5: sortedShoes }),
    ...(!!Object.keys(otherGroups).length && { ...otherGroups }),
  };
};

// This function and the ones inside are part of an AB test, might be removed in the future
/**
 * Groups an array of items by their size group and sorts the grouped items.
 *
 * The function iterates over the provided items, accumulating them into an object
 * where each key is a size group and the corresponding value is an array of items
 * that belong to that group. After grouping, it sorts the size groups using the
 * `sortSizeGroups` function.
 *
 * @param {Array<Object>} items - An array of items to be grouped.
 * @returns {Object} - An object containing grouped items, where each key is a size group
 * and each value is an array of items in that group.
 */
export const formatSizeGroups = (items) => {
  const groupedItems = items.reduce((accum, item) => {
    let sizeGroupItems = accum[item.sizeGroup] || [];
    sizeGroupItems = [...sizeGroupItems, item];
    return { ...accum, [item.sizeGroup]: sizeGroupItems };
  }, {});

  return sortSizeGroups(groupedItems);
};

export const titlesBySizeGroup = {
  1: {
    title: "General",
    subtitle: "",
  },
  2: {
    title: "Fixed Waist Bottoms",
    subtitle: "Waistband with buttons and zippers",
  },
  3: { title: "Bra Band/Cup", subtitle: "" },
  5: { title: "Shoes" },
};

export const getGenderFromTitle = (title = "") => {
  if (title.toLowerCase().includes("women")) return "women";
  if (title.toLowerCase().includes("men")) return "men";
  return "";
};

const nValuesEquivalencies = {
  categories: {
    sale: { "en-US": "8t6", "en-CA": "8t5" },
    bestsellers: { "en-US": "8td", "en-CA": "8tb" },
    "whats-new": { "en-US": "8te", "en-CA": "8tc" },
    shoes: { "en-US": "8tj", "en-CA": "8ti" },
  },
  genders: {
    women: "1z0xcuu",
    men: "1z0xcmk",
  },
  shoes: "1z0sx8b",
  sizes: {
    5: "1z11uwf",
    5.5: "1z11uwe",
    6: "1z140v0",
    6.5: "1z11uwd",
    7: "1z11uwx",
    7.5: "1z11uww",
    8: "1z140uy",
    8.5: "1z11uwv",
    9: "1z11uwu",
    9.5: "1z11uwt",
    10: "1z140v8",
    10.5: "1z11ux3",
    11: "1z11ux2",
    11.5: "1z11ux1",
    12: "1z140v6",
    12.5: "1z11ux0",
    13: "1z11uwz",
    14: "1z140ut",
  },
};

/**
 * Builds a URL path for filtering shoes by category, locale, gender, and size.
 *
 * @param {string} category - The category of the product (e.g., 'sale', 'bestsellers', etc.).
 * @param {string} locale - The locale (e.g., 'en-US', 'en-CA').
 * @param {string} gender - The gender associated with the product (e.g., 'women', 'men').
 * @param {string} size - The size of the shoes (e.g., '7', '8', etc.).
 *
 * @returns {string} The constructed URL path for the shoes size filter.
 */
export const buildShoesSizeFilterNValue = (
  category,
  locale,
  gender,
  size,
  currentNavigationState
) => {
  const { categories, genders, sizes, shoes } = nValuesEquivalencies;
  const shouldRedirectToShoesCategory = ["women", "men"].includes(category);
  const categoryPath = shouldRedirectToShoesCategory ? "shoes" : category;
  const genderNValue = genders[`${gender}`];
  const shoesNValue = shouldRedirectToShoesCategory ? "" : shoes;
  const sizeNValue = sizes[`${size}`];
  const categoryNValue = shouldRedirectToShoesCategory
    ? categories.shoes[`${locale}`]
    : categories[`${category}`]?.[`${locale}`];

  if (!genderNValue || !categoryNValue) {
    return currentNavigationState;
  }

  return `/${categoryPath}/_/N-${genderNValue}Z${shoesNValue}Z${sizeNValue}Z${categoryNValue}`;
};

/**
 * Splits shoe sizes by giving them a new size group. Gives a new size group for sizes
 * not mapped in current experience (size group 6). If the page has the 'Shoes' filter
 * applied, then the rest of the sizes are removed and it keeps only the shoes sizes to display
 *
 * @param {string} params.locale - The locale (e.g., 'en-US', 'en-CA').
 * @param {string} params.gender - The gender associated with the product (e.g., 'women', 'men').
 * @param {string} params.category - The category of the product (e.g., 'sale', 'bestsellers', etc.).
 * @param {Array<Object>} params.sizes - An array of size objects.
 * @param {string} params.URLPath - The current URL path for determining context.
 *
 * @returns {Array<Object>} An array of size objects with updated size groups.
 */
export const splitShoesSizes = ({
  locale,
  gender,
  category,
  sizes,
  URLPath,
}) => {
  const sizesWithShoesSizes = [];
  sizes.map((size) => {
    const isShoeSize = nValuesEquivalencies.sizes[size.label];
    const isOnlyShoesPage = URLPath.includes(nValuesEquivalencies.shoes);
    if (size[`${GROUPING_ABTEST_QUERY_KEY}`] >= 5) {
      return sizesWithShoesSizes.push({
        ...size,
        sizeGroup: 6,
      });
    }
    if (isShoeSize) {
      sizesWithShoesSizes.push({
        ...size,
        sizeGroup: 5,
        "navigation-state": buildShoesSizeFilterNValue(
          category,
          locale,
          gender,
          size.label,
          size["navigation-state"]
        ),
      });
    }
    if (!isOnlyShoesPage) {
      sizesWithShoesSizes.push({
        ...size,
        sizeGroup: size[`${GROUPING_ABTEST_QUERY_KEY}`],
      });
    }
  });
  return sizesWithShoesSizes;
};

const categoriesNValuesByLocale = {
  "en-US": {
    sale: "8t6",
    bestsellers: "8td",
    "whats-new": "8te",
    women: "7vf",
    men: "7tu",
  },
  "en-CA": {
    sale: "8t5",
    bestsellers: "8tb",
    "whats-new": "8tc",
    women: "7z5",
    men: "7qr",
  },
};

export const shouldUseABTGrouping = (URLPath, locale, category) => {
  const [nValue] = URLPath.split("/").reverse();
  const categoryNValuePath =
    categoriesNValuesByLocale[`${locale}`]?.[`${category}`];
  return nValue?.includes(categoryNValuePath);
};
